import { useEffect } from 'react'

import { deepSetObject, toggleArray } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import type { EntitiesState } from '@redux/entities'

import type { TemplatePageModel } from '@models/types'

import useTemplatePage from '@hooks/useTemplatePage'

import useScopedEntities from './useScopedEntities'
import type { FilteredEntities } from './useScopedEntities'

const nextStepKeys = {
  providerIds: 'serviceProductIds',
  serviceProductIds: 'agencyIds',
  agencyIds: 'publicationIds',
  publicationIds: 'templateGroupIds',
  templateGroupIds: 'brandThemeIds',
}

type PermissionGroup = {
  agencyIds: number[],
  brandThemeIds: number[],
  providerIds: number[],
  publicationIds: number[],
  serviceProductIds: number[],
  templateGroupIds: number[],
}

export type Permissions = {
  current: PermissionGroup,
  queued: PermissionGroup,
}

const defaultState = {
  activeSegmentKey: 'all',
  activeStepKey: 'providerIds',
  dirtyEntityKeys: {
    agencyIds: false,
    brandThemeIds: false,
    providerIds: false,
    publicationIds: false,
    serviceProductIds: false,
    templateGroupIds: false,
  },
  filterString: '',
  permissions: {
    current: {
      agencyIds: [],
      brandThemeIds: [],
      providerIds: [],
      publicationIds: [],
      serviceProductIds: [],
      templateGroupIds: [],
    },
    queued: {
      agencyIds: [],
      brandThemeIds: [],
      providerIds: [],
      publicationIds: [],
      serviceProductIds: [],
      templateGroupIds: [],
    },
  },
}

const updateQueuedEntityIds = (params: {
  entityKey: string,
  entityIds: number[],
  state: typeof defaultState,
  setState: (state: Partial<typeof defaultState>) => void,
}) => {
  const {
    entityIds, entityKey, setState, state,
  } = params

  // @ts-expect-error TS2345
  const updatedQueuedPermissions = deepSetObject(state, `permissions.queued.${entityKey}`, entityIds)
  const updatedDirtyEntityKeys = deepSetObject(updatedQueuedPermissions, `dirtyEntityKeys.${entityKey}`, true)

  // @ts-expect-error TS2345
  setState(updatedDirtyEntityKeys)
}

const bulkDeselectEntityIds = (params: {
  entityKey: string,
  entityIds: number[],
  permissions: Permissions,
  state: typeof defaultState,
  setState: (state: Partial<typeof defaultState>) => void,
}) => {
  const {
    entityIds, entityKey, permissions, setState, state,
  } = params

  // @ts-expect-error TS7053
  const currentEntityIds = permissions.queued[entityKey] ?? []
  // @ts-expect-error TS7006
  const filteredEntityIds = currentEntityIds.filter(id => !entityIds.includes(id))

  updateQueuedEntityIds({
    entityKey,
    entityIds: filteredEntityIds,
    state,
    setState,
  })
}

const bulkSelectEntityIds = (params: {
  entityKey: string,
  entityIds: number[],
  permissions: Permissions,
  state: typeof defaultState,
  setState: (state: Partial<typeof defaultState>) => void,
}) => {
  const {
    entityIds, entityKey, permissions, setState, state,
  } = params

  // @ts-expect-error TS7053
  const currentEntityIds = permissions.queued[entityKey] ?? []

  const mergedIds = [...currentEntityIds, ...entityIds]
  const uniqueIds = Array.from(new Set(mergedIds))

  updateQueuedEntityIds({
    entityKey,
    entityIds: uniqueIds,
    state,
    setState,
  })
}

const filterUnassignedEntityIds = (params: { currentIds: number[], entityArray: object[] }) => {
  const { currentIds, entityArray } = params

  // @ts-expect-error TS2339
  const entityIds = entityArray.map(entity => entity.id)
  const filteredIds = currentIds.filter(id => entityIds.includes(id))

  return filteredIds
}

const filterUnassignedEntities = (params: {
  entityKey: string,
  entities: Partial<EntitiesState>,
  permissions: Permissions,
}) => {
  const { entities, entityKey, permissions } = params

  // @ts-expect-error TS7053
  const currentEntityIds = permissions.current[entityKey] ?? []
  // @ts-expect-error TS7053
  const queuedEntityIds = permissions.queued[entityKey] ?? []

  // @ts-expect-error TS7006
  const unassignedEntityIds = currentEntityIds.filter(id => !queuedEntityIds.includes(id))

  // @ts-expect-error TS7006
  const mapped = unassignedEntityIds.map((id) => {
    // @ts-expect-error TS7053
    const entity = entities[id] ?? { id, title: 'Unassigned Entity' }
    return entity
  })

  return mapped
}

const filterVisibleEntities = (params: {
  activeSegmentKey: string,
  entityKey: string,
  entities: object[],
  permissions: Permissions,
}) => {
  const {
    activeSegmentKey, entities, entityKey, permissions,
  } = params

  if (activeSegmentKey === 'selected'){
    // @ts-expect-error TS7053
    const queuedEntityIds = permissions.queued[entityKey] ?? []
    const filtered = entities.filter((entity: object) => {
      // @ts-expect-error TS2339
      const { id } = entity
      return queuedEntityIds.includes(id)
    })

    return filtered
  }

  return entities
}

type MergeUniqueIdsParams = {
  currentPermissions: {
    [key: string]: number[],
  },
  dirtyEntityKeys: typeof defaultState['dirtyEntityKeys'],
  entityArray: object[],
  entityKey: string,
  permissions: Permissions,
}

const mergeAndFilterUniqueIds = (params: MergeUniqueIdsParams) => {
  const {
    currentPermissions,
    dirtyEntityKeys,
    entityArray,
    entityKey,
    permissions: {
      queued: queuedPermissions,
    },
  } = params

  // @ts-expect-error TS7053
  const isEntityKeyDirty = dirtyEntityKeys[entityKey]
  // @ts-expect-error TS7053
  const queuedPermissionIds = queuedPermissions[entityKey]

  // Only use the current/saved template page permissions
  // if the user hasn't toggled entities
  const mergedIds = isEntityKeyDirty
    ? queuedPermissionIds
    : [...currentPermissions[entityKey], ...queuedPermissionIds]

  // Remove any ids that are not in the filtered array
  // e.g. user deselects a provider that was required
  // for a certain product
  const assignedEntityIds = filterUnassignedEntityIds({
    currentIds: mergedIds,
    entityArray,
  })

  const uniqueIds = new Set(assignedEntityIds)

  return Array.from(uniqueIds)
}

type PreparePermissionsParams = {
  state: typeof defaultState,
}

const preparePermissions = (params: PreparePermissionsParams) => {
  const { state: { dirtyEntityKeys, permissions: { queued: queuedPermissionIds } } } = params

  const mappedPermissions = {
    agency_ids: dirtyEntityKeys.agencyIds ? queuedPermissionIds.agencyIds : undefined,
    brand_theme_ids: dirtyEntityKeys.brandThemeIds ? queuedPermissionIds.brandThemeIds : undefined,
    provider_ids: dirtyEntityKeys.providerIds ? queuedPermissionIds.providerIds : undefined,
    publication_ids: dirtyEntityKeys.publicationIds ? queuedPermissionIds.publicationIds : undefined,
    service_product_ids: dirtyEntityKeys.serviceProductIds ? queuedPermissionIds.serviceProductIds : undefined,
    template_group_ids: dirtyEntityKeys.templateGroupIds ? queuedPermissionIds.templateGroupIds : undefined,
  }

  return mappedPermissions
}

type SetupPermissionsParams = {
  callbacks: {
    setState: (state: Partial<typeof defaultState>) => void,
  },
  dirtyEntityKeys: typeof defaultState['dirtyEntityKeys'],
  filteredEntities: FilteredEntities,
  templatePage: TemplatePageModel,
  permissions: Permissions,
}

const setupPermissions = (params: SetupPermissionsParams) => {
  const {
    callbacks: { setState },
    dirtyEntityKeys,
    filteredEntities: {
      agencies, brandThemes, providers, publications, serviceProducts, templateGroups,
    },
    permissions,
    templatePage,
  } = params

  const currentPermissions = {
    agencyIds: templatePage?.permission_ids?.agencies ?? [],
    brandThemeIds: templatePage?.permission_ids?.brand_themes ?? [],
    providerIds: templatePage?.permission_ids?.providers ?? [],
    publicationIds: templatePage?.permission_ids?.publications ?? [],
    serviceProductIds: templatePage?.permission_ids?.products ?? [],
    templateGroupIds: templatePage?.permission_ids?.template_groups ?? [],
  }

  const queuedPermissions = {
    agencyIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: agencies, entityKey: 'agencyIds', permissions,
    }),
    brandThemeIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: brandThemes, entityKey: 'brandThemeIds', permissions,
    }),
    providerIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: providers, entityKey: 'providerIds', permissions,
    }),
    publicationIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: publications, entityKey: 'publicationIds', permissions,
    }),
    serviceProductIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: serviceProducts, entityKey: 'serviceProductIds', permissions,
    }),
    templateGroupIds: mergeAndFilterUniqueIds({
      currentPermissions, dirtyEntityKeys, entityArray: templateGroups, entityKey: 'templateGroupIds', permissions,
    }),
  }

  const updatedState = {
    permissions: {
      current: currentPermissions,
      queued: queuedPermissions,
    },
  }

  // @ts-expect-error TS2345
  setState(updatedState)
}

type UseAssignTemplatePagePermissionsModal = {
  activeStepKey?: string,
  templatePageId: number,
}

function useAssignTemplatePagePermissionsModal(options: UseAssignTemplatePagePermissionsModal) {
  const { activeStepKey: initActiveStepKey, templatePageId } = options

  const [state, setState] = useSetState(defaultState)
  const {
    activeSegmentKey,
    activeStepKey,
    dirtyEntityKeys,
    filterString,
    permissions,
    permissions: {
      queued: queuedPermissionIds,
    },
  } = state

  const templatePagePayload = useTemplatePage({ id: templatePageId })
  const {
    templatePage, templatePage: {
      id, brand_id, cache_key, permission_ids, service_id,
    },
  } = templatePagePayload

  useEffect(() => {
    if (initActiveStepKey && permission_ids?.providers?.length){
      setState({ activeStepKey: initActiveStepKey })
    }
  }, [initActiveStepKey])

  const scopedEntityPayload = useScopedEntities({
    activeStepKey,
    filters: {
      brand_id,
      service_id,
      string: filterString,
    },
    permissions,
  })

  const { entitiesUpdatedAt, filteredEntities, payloads } = scopedEntityPayload

  useEffect(() => {
    if (id){
      setupPermissions({
        callbacks: {
          setState,
        },
        dirtyEntityKeys,
        filteredEntities,
        permissions,
        // @ts-expect-error
        templatePage,
      })
    }
  }, [activeStepKey, id, cache_key, entitiesUpdatedAt])

  const nextStepKey = nextStepKeys[activeStepKey as keyof typeof nextStepKeys]
  const hasNextStep = !!nextStepKey

  const saveEnabled = !!queuedPermissionIds.providerIds.length
    && !!queuedPermissionIds.serviceProductIds.length
    && !!queuedPermissionIds.agencyIds.length

  return {
    activeSegmentKey,
    activeStepKey,
    callbacks: {
      bulkDeselectEntityIds: (params: { entityKey: string, entityIds: number[] }) => {
        const { entityKey, entityIds } = params

        return bulkDeselectEntityIds({
          entityIds,
          entityKey,
          permissions,
          setState,
          state,
        })
      },
      bulkSelectEntityIds: (params: { entityKey: string, entityIds: number[] }) => {
        const { entityIds, entityKey } = params

        return bulkSelectEntityIds({
          entityIds,
          entityKey,
          permissions,
          setState,
          state,
        })
      },
      filterUnassignedEntities: (params: {
        entityKey: string,
        entities: Partial<EntitiesState>,
      }) => {
        const { entityKey, entities } = params

        return filterUnassignedEntities({
          entities,
          entityKey,
          permissions,
        })
      },
      filterVisibleEntities: (params: {
        entityKey: string,
        entities: object[],
      }) => {
        const { entities, entityKey } = params

        return filterVisibleEntities({
          activeSegmentKey,
          entityKey,
          entities,
          permissions,
        })
      },
      preparePermissions: () => (
        preparePermissions({
          state,
        })
      ),
      setCurrentSegmentKey: (key: string) => setState({ activeSegmentKey: key, filterString: '' }),
      setCurrentStepKey: (key: string) => {
        setState({ activeStepKey: key })
        setState({ filterString: '' })
      },
      setState,
      toggleEntityId: (params: { entityKey: string, entityId: number }) => {
        const { entityId, entityKey } = params

        // @ts-expect-error TS7053
        const updatedEntityIds = toggleArray(permissions.queued[entityKey], entityId)

        updateQueuedEntityIds({
          entityKey,
          entityIds: updatedEntityIds,
          state,
          setState,
        })
      },
    },
    filterString,
    hasNextStep,
    nextStepKey,
    payloads,
    permissions,
    saveEnabled,
    templatePage,
  }
}

export default useAssignTemplatePagePermissionsModal
